export const normalizeTimeZoneShort = (label = "") => {
  const normalized = String(label || "").trim();

  return {
    EDT: "ET",
    EST: "ET",
    CDT: "CT",
    CST: "CT",
    MDT: "MT",
    MST: "MT",
    PDT: "PT",
    PST: "PT",
  }[normalized] || normalized;
};

const DEFAULT_SOURCE_TIME_ZONE = "America/Los_Angeles";
const EXPLICIT_TIME_ZONE_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const LOCAL_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?)?$/;
const dateTimeFormatters = new Map();

const getFormatter = (key, options) => {
  const cached = dateTimeFormatters.get(key);
  if (cached) {
    return cached;
  }

  const formatter = new Intl.DateTimeFormat("en-US", options);
  dateTimeFormatters.set(key, formatter);
  return formatter;
};

const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

const getUserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (e) {
    return undefined;
  }
};

const getPartMap = (date, formatter) => (
  formatter.formatToParts(date).reduce((parts, part) => {
    if (part.type !== "literal") {
      parts[part.type] = part.value;
    }

    return parts;
  }, {})
);

const getTimeZoneOffsetMs = (date, timeZone) => {
  const formatter = getFormatter(`offset:${timeZone}`, {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = getPartMap(date, formatter);
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour === "24" ? "0" : parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return asUtc - date.getTime();
};

const buildDateInTimeZone = (match, timeZone) => {
  const utcGuess = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4] || 0),
    Number(match[5] || 0),
    Number(match[6] || 0)
  );
  const firstOffset = getTimeZoneOffsetMs(new Date(utcGuess), timeZone);
  const firstDate = new Date(utcGuess - firstOffset);
  const secondOffset = getTimeZoneOffsetMs(firstDate, timeZone);

  return new Date(utcGuess - secondOffset);
};

export const parseBoardStartDate = (raw, sourceTimeZone = DEFAULT_SOURCE_TIME_ZONE) => {
  if (!raw) {
    return null;
  }

  if (raw instanceof Date) {
    return isValidDate(raw) ? raw : null;
  }

  if (typeof raw === "number") {
    const date = new Date(raw);
    return isValidDate(date) ? date : null;
  }

  if (typeof raw !== "string") {
    return null;
  }

  const value = raw.trim();
  if (!value) {
    return null;
  }

  if (EXPLICIT_TIME_ZONE_PATTERN.test(value)) {
    const date = new Date(value);
    return isValidDate(date) ? date : null;
  }

  const localMatch = value.match(LOCAL_DATE_TIME_PATTERN);
  if (localMatch) {
    return buildDateInTimeZone(localMatch, sourceTimeZone);
  }

  const fallbackDate = new Date(value.replace(" ", "T"));
  return isValidDate(fallbackDate) ? fallbackDate : null;
};

const isSameCalendarDay = (leftDate, rightDate, timeZone) => {
  const formatter = getFormatter(`day:${timeZone || "default"}`, {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(leftDate) === formatter.format(rightDate);
};

const formatTimeParts = (date, timeZone) => {
  const formatter = getFormatter(`time:${timeZone || "default"}`, {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
  const parts = getPartMap(date, formatter);
  const zoneLabel = normalizeTimeZoneShort(parts.timeZoneName);

  return `${parts.hour}:${parts.minute} ${parts.dayPeriod}${zoneLabel ? ` ${zoneLabel}` : ""}`;
};

const formatWeekday = (date, timeZone, includePeriod) => {
  const formatter = getFormatter(`weekday:${timeZone || "default"}`, {
    timeZone,
    weekday: "short",
  });
  const weekday = formatter.format(date);

  return `${weekday}${includePeriod ? "." : ""}`;
};

export const formatBoardStartTime = (
  raw,
  {
    fallback = "",
    sourceTimeZone = DEFAULT_SOURCE_TIME_ZONE,
    todayLabel = "",
    weekdayPeriod = false,
  } = {}
) => {
  const date = parseBoardStartDate(raw, sourceTimeZone);
  if (!date) {
    return fallback;
  }

  const userTimeZone = getUserTimeZone();
  const timeText = formatTimeParts(date, userTimeZone);
  const prefix = isSameCalendarDay(date, new Date(), userTimeZone)
    ? todayLabel
    : formatWeekday(date, userTimeZone, weekdayPeriod);

  return [prefix, timeText].filter(Boolean).join(" ");
};
